// @ts-check
import React, { StatelessComponent } from 'react'; // eslint-disable-line no-unused-vars
import PropTypes from 'prop-types';
import cx from 'classnames';
import { connect } from 'react-redux';
import _ from 'lodash';
import formatPhone from 'lib/formatPhone';
import MapPin from 'shared/components/MapPin';
import ProviderInfoCard from '../ProviderInfoCard';
import AvailableSlotsPicker from '../AvailableSlotsPicker';
import NoSlotsAvailablePicker from '../NoSlotsAvailablePicker';
import Icon from 'shared/components/Icon';
import classes from './LocationModal.module.scss';
import { ProviderMappingResult, ReferralProfile, AppointmentSlot, Location } from '../types'; // eslint-disable-line no-unused-vars

/** @typedef {{
  matchingResult: ProviderMappingResult,
  referralProfile: ReferralProfile,
  onSlotClick: (slot: AppointmentSlot) => void,
  onProviderClick: (result: ProviderMappingResult) => void
}} ProviderResultPropsType */

/** @type { React.ValidationMap<ProviderResultPropsType> } */
const providerResultPropTypes = {
  matchingResult: PropTypes.object.isRequired,
  referralProfile: PropTypes.object.isRequired,
  onSlotClick: PropTypes.func.isRequired,
  onProviderClick: PropTypes.func.isRequired,
};

/** @type { StatelessComponent<ProviderResultPropsType> } */
const ProviderResult = ({ matchingResult, referralProfile, onSlotClick, onProviderClick }) => {
  const { earliestAppointmentTime } = matchingResult;
  const hasSlots = !!earliestAppointmentTime;

  return (
    <div className={classes.ProviderResult}>
      <ProviderInfoCard matchingResult={matchingResult} referralProfile={referralProfile} />

      <div className={classes.slot_picker_container}>
        {hasSlots ? (
          <AvailableSlotsPicker matchingResult={matchingResult} onSlotClick={onSlotClick} />
        ) : (
          <NoSlotsAvailablePicker
            matchingResult={matchingResult}
            onProviderClick={onProviderClick}
          />
        )}
      </div>
    </div>
  );
};

ProviderResult.displayName = 'ProviderResult';
ProviderResult.propTypes = providerResultPropTypes;

/** @typedef {{
  location: Location,
  referralProfile: ReferralProfile,
  matchingResults: ProviderMappingResult[],
  onLocationClick: (location: Location) => void,
  onSlotClick: (slot: AppointmentSlot) => void,
  onProviderClick: (result: ProviderMappingResult) => void
}} LocationModalPropsType */

/**
 * @todo: these ought to be required, but don't exist when modal is not open
 * @type { React.ValidationMap<LocationModalPropsType> }
 */
const propTypes = {
  location: PropTypes.object,
  referralProfile: PropTypes.object,
  matchingResults: PropTypes.array,
  onLocationClick: PropTypes.func,
  onSlotClick: PropTypes.func,
  onProviderClick: PropTypes.func,
};

/** @type { StatelessComponent<LocationModalPropsType> } */
const LocationModal = ({
  location,
  referralProfile,
  matchingResults,
  onLocationClick,
  onSlotClick,
  onProviderClick,
}) => {
  const { address } = location;

  return (
    <div className={classes.LocationModal}>
      <div className={classes.heading}>
        <div className={classes.map_pin_container}>
          <MapPin />
        </div>
        <div className={classes.info}>
          <div>
            <h3>
              {location.name}
              <span className={classes.distance}>
                <Icon name="compass" /> {Math.round(matchingResults[0].distance * 100) / 100} mi
              </span>
            </h3>
            <div className={classes.address}>
              <p>{address.streetAddress}</p>
              {address.streetAddressLine2 ? <p>{address.streetAddressLine2}</p> : null}
              <p>
                {address.city}, {address.state} {address.zip}
              </p>
              <p>
                <span className={classes.phone}>
                  <Icon name="phone" /> {location.phone ? formatPhone(location.phone) : 'Unknown'}
                </span>
                <span className={classes.phone}>
                  <Icon name="fax" /> {location.fax ? formatPhone(location.fax) : 'Unknown'}
                </span>
              </p>
            </div>
          </div>
        </div>
        <div className={classes.modal_header_action_wrapper}>
          <a
            onClick={() => onLocationClick(location)}
            className={cx('outlined-action-button', classes.action_btn)}
          >
            Refer to Location
          </a>
        </div>
      </div>
      <div>
        {matchingResults.map(r => (
          <ProviderResult
            key={r.id}
            matchingResult={r}
            referralProfile={referralProfile}
            onSlotClick={onSlotClick}
            onProviderClick={onProviderClick}
          />
        ))}
      </div>
    </div>
  );
};

LocationModal.displayName = 'LocationModal';
LocationModal.propTypes = propTypes;

const mapStateToProps = (state, ownProps) => {
  const { pageState, entities } = state.referralDashboardResults;
  const locationId = ownProps.locationId;
  const resultsWithProviders = _.values(entities.matchingResults)
    .filter(r => r.location === locationId)
    .map(r => ({
      ...r,
      provider: entities.providers[r.provider],
    }));

  return {
    location: entities.locations[locationId],
    referralProfile: entities.referralProfiles[pageState.referralProfileId],
    matchingResults: resultsWithProviders,
  };
};

export default connect(mapStateToProps)(LocationModal);
